import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MessagesInbox } from "@/components/admin/MessagesInbox";
import { StripeKeyCard } from "@/components/admin/StripeKeyCard";
import { MonthlyReport } from "@/components/admin/MonthlyReport";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  ShieldCheck, Users, Crown, Clapperboard, DollarSign, Coins,
  CalendarClock, Send, TrendingUp, Lock, Mail, Megaphone, Image as ImageIcon,
} from "lucide-react";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

function SecurityCard({ pin, onPinChanged }) {
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [code, setCode] = useState({ current: "", next: "" });
  const [busyPwd, setBusyPwd] = useState(false);
  const [busyPin, setBusyPin] = useState(false);
  const input = "w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pwd.next !== pwd.confirm) return toast.error("New passwords don't match");
    setBusyPwd(true);
    try {
      await api.put("/auth/password", { current_password: pwd.current, new_password: pwd.next, sign_out_all: false });
      toast.success("Admin password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyPwd(false);
    }
  };

  const changePin = async (e) => {
    e.preventDefault();
    if (code.next.trim().length < 6) return toast.error("New entry code must be at least 6 characters");
    setBusyPin(true);
    try {
      await api.post("/admin/security/pin", { current_pin: code.current.trim(), new_pin: code.next.trim() }, { headers: { "X-Admin-Pin": pin } });
      sessionStorage.setItem("vi_admin_pin", code.next.trim());
      onPinChanged(code.next.trim());
      toast.success("Admin entry code changed — use the new code from now on");
      setCode({ current: "", next: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyPin(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-security-card">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="h-4 w-4 text-amber-400" />
        <h2 className="font-head text-lg font-bold">Security</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Rotate your admin login password and the dashboard entry code whenever you need.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form onSubmit={changePassword} className="space-y-3" data-testid="admin-password-form">
          <p className="text-sm font-semibold text-zinc-300">Change admin password</p>
          <input type="password" required value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))} placeholder="Current password" autoComplete="current-password" data-testid="admin-password-current" className={input} />
          <input type="password" required value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} placeholder="New password (min 6 characters)" autoComplete="new-password" data-testid="admin-password-new" className={input} />
          <input type="password" required value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} placeholder="Confirm new password" autoComplete="new-password" data-testid="admin-password-confirm" className={input} />
          <button type="submit" disabled={busyPwd} data-testid="admin-password-submit" className="w-full text-sm bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60">
            {busyPwd ? "Saving…" : "Change password"}
          </button>
        </form>
        <form onSubmit={changePin} className="space-y-3" data-testid="admin-pin-change-form">
          <p className="text-sm font-semibold text-zinc-300">Change admin entry code</p>
          <input type="password" required value={code.current} onChange={(e) => setCode((c) => ({ ...c, current: e.target.value }))} placeholder="Current entry code" autoComplete="off" data-testid="admin-pin-current" className={input} />
          <input type="password" required value={code.next} onChange={(e) => setCode((c) => ({ ...c, next: e.target.value }))} placeholder="New entry code (6-32 characters)" autoComplete="off" data-testid="admin-pin-new" className={input} />
          <button type="submit" disabled={busyPin} data-testid="admin-pin-submit-change" className="w-full text-sm bg-amber-600 hover:bg-amber-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60">
            {busyPin ? "Saving…" : "Change entry code"}
          </button>
          <p className="text-[11px] text-zinc-600">The new code takes effect immediately — this session stays unlocked with it.</p>
        </form>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.05] transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <div className="mt-3 text-3xl font-head font-extrabold tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
  </div>
);

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/verify-pin", { pin: pin.trim() });
      sessionStorage.setItem("vi_admin_pin", pin.trim());
      onUnlock(pin.trim());
    } catch (err) {
      toast.error(apiError(err));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <form onSubmit={submit} data-testid="admin-pin-form" className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-xl bg-blue-600/20 flex items-center justify-center">
          <Lock className="h-6 w-6 text-blue-400" />
        </div>
        <h1 className="mt-4 font-head text-2xl font-extrabold">Admin Access</h1>
        <p className="mt-1 text-sm text-zinc-500">Enter your PIN to view the dashboard.</p>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          data-testid="admin-pin-input"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="••••••"
          className="mt-6 w-full text-center tracking-[0.5em] text-lg rounded-lg bg-black/40 border border-white/10 px-4 py-3 outline-none focus:border-blue-500 transition-colors"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-pin-submit"
          className="mt-4 w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-3 rounded-lg text-sm font-semibold transition-colors"
        >
          {busy ? "Verifying…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [pin, setPin] = useState(() => sessionStorage.getItem("vi_admin_pin") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [promoText, setPromoText] = useState("");
  const [promoSaving, setPromoSaving] = useState(false);
  const [catSaving, setCatSaving] = useState(false);
  const [annBusy, setAnnBusy] = useState(false);

  const testAnnounce = async () => {
    setAnnBusy(true);
    try {
      const { data: r } = await api.post("/admin/createathon-announce-test", {}, { headers: { "X-Admin-Pin": pin } });
      toast.success(`Test announcement sent to ${r.sent_to}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAnnBusy(false);
    }
  };

  const sendAnnounce = async (force = false) => {
    if (!force && !window.confirm("Send the Create-a-thon announcement to ALL newsletter subscribers?")) return;
    setAnnBusy(true);
    try {
      const { data: r } = await api.post("/admin/createathon-announce", { force }, { headers: { "X-Admin-Pin": pin } });
      toast.success(`Announcement sent to ${r.sent} subscriber${r.sent === 1 ? "" : "s"}.`);
      load(pin);
    } catch (err) {
      if (err?.response?.status === 409) {
        if (window.confirm("Already announced for this run. Send again anyway?")) { setAnnBusy(false); return sendAnnounce(true); }
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setAnnBusy(false);
    }
  };

  const saveCreateathon = async (active) => {
    setCatSaving(true);
    try {
      await api.post("/admin/createathon", { active }, { headers: { "X-Admin-Pin": pin } });
      toast.success(active ? "Create-a-thon is live." : "Create-a-thon turned off.");
      load(pin);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCatSaving(false);
    }
  };

  const savePromo = async (active) => {
    setPromoSaving(true);
    try {
      await api.post("/admin/promo", { text: promoText, active }, { headers: { "X-Admin-Pin": pin } });
      toast.success(active ? "Special is live on the pricing page." : "Special turned off.");
      load(pin);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPromoSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setTestSending(true);
    try {
      const { data: r } = await api.post("/admin/digest-test-send", {}, { headers: { "X-Admin-Pin": pin } });
      toast.success(`Test email sent to ${r.sent_to}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTestSending(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data: p } = await api.get("/admin/digest-preview", { headers: { "X-Admin-Pin": pin } });
      setPreview(p);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/metrics", { headers: { "X-Admin-Pin": p } });
      setData(data);
      if (data.promo?.text) setPromoText(data.promo.text);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("vi_admin_pin");
        setPin("");
      }
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pin) load(pin);
  }, [pin, load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!pin) return <PinGate onUnlock={setPin} />;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-blue-400" />
        </div>
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Admin Dashboard</h1>
          <p className="text-sm text-zinc-500">Live overview of subscribers, content & revenue.</p>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-20 flex justify-center">
          <div className="h-10 w-10 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Revenue headline */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard testid="metric-total-revenue" icon={TrendingUp} accent="text-emerald-400" label="Total Revenue" value={fmtMoney(data.revenue.total)} sub="Subscriptions + credit packs" />
            <StatCard testid="metric-sub-revenue" icon={DollarSign} accent="text-emerald-400" label="Subscription Sales" value={fmtMoney(data.revenue.subscriptions)} sub={`${fmtNum(data.revenue.subscription_count)} payments`} />
            <StatCard testid="metric-credit-revenue" icon={Coins} accent="text-yellow-400" label="Credit Sales" value={fmtMoney(data.revenue.credits)} sub={`${fmtNum(data.revenue.credit_count)} orders`} />
          </div>

          {/* Core counts */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="metric-users" icon={Users} accent="text-blue-400" label="Total Users" value={fmtNum(data.users.total)} sub={`+${fmtNum(data.users.new_30d)} last 30d`} />
            <StatCard testid="metric-subscribers" icon={Crown} accent="text-purple-400" label="Subscribers" value={fmtNum(data.users.subscribers)} sub="Paid plans" />
            <StatCard testid="metric-videos" icon={Clapperboard} accent="text-pink-400" label="Videos Made" value={fmtNum(data.videos.total)} sub={`+${fmtNum(data.videos.new_30d)} last 30d`} />
            <StatCard testid="metric-posts" icon={Send} accent="text-cyan-400" label="Published Posts" value={fmtNum(data.posts.published)} sub={`${fmtNum(data.posts.scheduled)} scheduled`} />
          </div>

          {/* Monthly stats + downloadable reports */}
          <MonthlyReport pin={pin} />

          {/* Contact inbox */}
          <MessagesInbox pin={pin} />

          {/* Stripe payments config */}
          <StripeKeyCard pin={pin} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan breakdown */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="plan-breakdown">
              <h2 className="font-head font-bold text-lg flex items-center gap-2"><Crown className="h-4 w-4 text-purple-400" /> Plan Breakdown</h2>
              <div className="mt-4 space-y-3">
                {(() => {
                  const total = Object.values(data.plan_breakdown).reduce((s, n) => s + n, 0) || 1;
                  return Object.entries(data.plan_breakdown).sort((a, b) => b[1] - a[1]).map(([plan, count]) => {
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={plan}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize text-zinc-300">{plan}</span>
                        <span className="text-zinc-500">{fmtNum(count)} ({pct}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                });
                })()}
              </div>
            </div>

            {/* Recent sales */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="recent-sales">
              <h2 className="font-head font-bold text-lg flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-400" /> Recent Sales</h2>
              <div className="mt-4 space-y-2">
                {data.recent_sales.length === 0 && <p className="text-sm text-zinc-500">No sales yet.</p>}
                {data.recent_sales.map((s, i) => (
                  <div key={`${s.email}-${s.created_at}-${i}`} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-0">
                    <div className="min-w-0">
                      <p className="truncate text-zinc-300">{s.email}</p>
                      <p className="text-xs text-zinc-500 capitalize">{s.kind === "subscription" ? `${s.plan_id} plan` : `${fmtNum(s.credits)} credits`} · {fmtDate(s.created_at)}</p>
                    </div>
                    <span className="font-semibold text-emerald-400 shrink-0">{fmtMoney(s.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent signups */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="recent-signups">
            <h2 className="font-head font-bold text-lg flex items-center gap-2"><Users className="h-4 w-4 text-blue-400" /> Recent Signups</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Plan</th>
                    <th className="pb-2 font-medium text-right">Credits</th>
                    <th className="pb-2 font-medium text-right">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_signups.map((u) => (
                    <tr key={u.email} className="border-t border-white/5">
                      <td className="py-2.5">
                        <p className="text-zinc-200">{u.name}</p>
                        <p className="text-xs text-zinc-500 truncate max-w-[200px]">{u.email}</p>
                      </td>
                      <td className="py-2.5"><span className="capitalize px-2 py-0.5 rounded-full bg-white/5 text-xs">{u.plan}</span></td>
                      <td className="py-2.5 text-right text-zinc-300">{fmtNum(u.credits)}</td>
                      <td className="py-2.5 text-right text-zinc-500">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Conversion funnel */}
          {data.funnel && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="conversion-funnel">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-violet-400" /> Conversion Funnel
              </h2>
              <div className="mt-4 space-y-2.5">
                {[
                  { key: "visitors", label: "Landing visitors" },
                  { key: "signups", label: "Signups" },
                  { key: "first_video", label: "Created a video" },
                  { key: "rendered", label: "Rendered / downloaded" },
                  { key: "subscribers", label: "Paid subscribers" },
                ].map((s, i, arr) => {
                  const val = data.funnel[s.key] || 0;
                  const prev = i === 0 ? null : data.funnel[arr[i - 1].key] || 0;
                  const max = Math.max(...arr.map((a) => data.funnel[a.key] || 0), 1);
                  const conv = prev ? Math.round((val / prev) * 100) : null;
                  return (
                    <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
                      <span className="text-xs text-zinc-500 w-40 shrink-0">{s.label}</span>
                      <div className="flex-1 h-6 bg-white/5 rounded-md overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-600/70 to-blue-500/70 rounded-md flex items-center px-2" style={{ width: `${Math.max(5, Math.round((val / max) * 100))}%` }}>
                          <span className="text-xs font-semibold text-white">{fmtNum(val)}</span>
                        </div>
                      </div>
                      <span className="text-xs text-zinc-500 w-14 text-right shrink-0">{conv === null ? "" : `${conv}%`}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-600 mt-3">Percentage = conversion from the previous stage. Visitor counting starts from this deploy.</p>
            </div>
          )}

          {/* Offer A/B test + exit intent */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="offer-ab-card">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <Send className="h-4 w-4 text-blue-400" /> Offer Email A/B Test
                <span className="ml-auto text-xs font-normal text-zinc-500">day-5 upgrade email</span>
              </h2>
              <div className="mt-4 space-y-3">
                {(data.offer_ab || []).map((v) => {
                  const ctr = v.sent ? Math.round((v.clicks / v.sent) * 100) : 0;
                  const label = v.variant === "A" ? "“Autopilot” angle" : "“Consistency” angle";
                  return (
                    <div key={v.variant} data-testid={`offer-variant-${v.variant}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-300"><span className="font-semibold text-white">Variant {v.variant}</span> · {label}</span>
                        <span className="text-zinc-500 text-xs">{fmtNum(v.sent)} sent · {fmtNum(v.clicks)} clicks · <span className="text-zinc-300 font-semibold">{ctr}% CTR</span></span>
                      </div>
                      <div className="mt-1.5 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${v.variant === "A" ? "bg-blue-500" : "bg-violet-500"}`} style={{ width: `${Math.min(100, ctr)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-600 mt-3" data-testid="offer-ab-leader">
                {(() => {
                  const ab = data.offer_ab || [];
                  const withSends = ab.filter((v) => v.sent > 0);
                  if (withSends.length < 2) return "Each new signup gets one of two email angles — the winner shows here once both have sends.";
                  const ctr = (v) => v.clicks / v.sent;
                  const [a, b] = ab;
                  if (ctr(a) === ctr(b)) return "Dead heat so far — both angles are drawing equal interest.";
                  const lead = ctr(a) > ctr(b) ? a : b;
                  return `Variant ${lead.variant} is drawing the most interest so far.`;
                })()}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="exit-intent-card">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-amber-400" /> Exit-Intent Popup
                <span className="ml-auto text-xs font-normal text-zinc-500">/pricing bounce capture</span>
              </h2>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-white/5 py-3" data-testid="exit-intent-shown">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.exit_intent?.shown)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Shown</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="exit-intent-captured">
                  <p className="text-2xl font-head font-extrabold text-emerald-400">{fmtNum(data.exit_intent?.captured)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Emails captured</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="exit-intent-rate">
                  <p className="text-2xl font-head font-extrabold">{data.exit_intent?.shown ? Math.round(((data.exit_intent?.captured || 0) / data.exit_intent.shown) * 100) : 0}%</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Capture rate</p>
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3">Fires once per visitor (7-day cooldown) when the cursor leaves the pricing page toward the browser bar.</p>
            </div>
            <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-600/[0.06] p-5" data-testid="createathon-card">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <Coins className="h-4 w-4 text-fuchsia-400" /> Video Create-a-thon
                <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${data.createathon?.active ? "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40" : "bg-white/5 text-zinc-500 border border-white/10"}`} data-testid="createathon-status-badge">
                  {data.createathon?.active ? "LIVE" : "OFF"}
                </span>
              </h2>
              <p className="text-xs text-zinc-500 mt-1">Neon banner on all public pages — first month of Starter $19 → $15 (20% off), auto-applied at checkout.</p>
              {data.createathon?.active && data.createathon?.ends_at && (
                <p className="text-xs text-amber-300 mt-1.5" data-testid="createathon-ends-note">
                  ⏳ Ends {new Date(data.createathon.ends_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} — banner shows a live countdown and switches off automatically.
                </p>
              )}
              {!data.createathon?.active && (
                <p className="text-xs text-zinc-400 mt-1.5" data-testid="createathon-ended-note">
                  Sale is off — the numbers below are the final results of the run.
                </p>
              )}
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-white/5 py-3" data-testid="createathon-joined">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.createathon?.joined)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Emails locked in</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="createathon-redeemed">
                  <p className="text-2xl font-head font-extrabold text-emerald-400">{fmtNum(data.createathon?.redeemed)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Redeemed</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="createathon-conv">
                  <p className="text-2xl font-head font-extrabold">{data.createathon?.joined ? Math.round(((data.createathon?.redeemed || 0) / data.createathon.joined) * 100) : 0}%</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Conversion</p>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-center">
                <div className="rounded-md bg-white/5 py-3" data-testid="createathon-revenue">
                  <p className="text-2xl font-head font-extrabold text-emerald-400">{fmtMoney(data.createathon?.revenue)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Sale revenue</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="createathon-newsletter-optins">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.createathon?.newsletter_optins)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Newsletter opt-ins</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => saveCreateathon(true)}
                  disabled={catSaving || data.createathon?.active}
                  data-testid="createathon-on-btn"
                  className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  Start 3-day run
                </button>
                <button
                  onClick={() => saveCreateathon(false)}
                  disabled={catSaving || !data.createathon?.active}
                  data-testid="createathon-off-btn"
                  className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  Turn off
                </button>
              </div>
              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid="createathon-announce-status">
                  <Megaphone className="h-3.5 w-3.5 text-fuchsia-400" /> Newsletter announcement
                  {data.createathon?.announced_at
                    ? ` · sent ${fmtDate(data.createathon.announced_at)} to ${fmtNum(data.createathon.announce_sent)} subscriber${data.createathon.announce_sent === 1 ? "" : "s"}`
                    : " · not sent yet"}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={testAnnounce}
                    disabled={annBusy}
                    data-testid="createathon-announce-test-btn"
                    className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    Test-send to me
                  </button>
                  <button
                    onClick={() => sendAnnounce()}
                    disabled={annBusy || !data.createathon?.active}
                    data-testid="createathon-announce-btn"
                    className="flex-1 bg-fuchsia-600/20 border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-600/35 px-3 py-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    {annBusy ? "Sending…" : "Send to subscribers"}
                  </button>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="ideogram-card">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-cyan-400" /> AI Provider Costs
              </h2>
              <p className="text-xs text-zinc-500 mt-1 flex items-center justify-between"><span>Ideogram images — Bold poster scenes + YouTube thumbnails</span><span className="text-zinc-600">est. $0.06 / image</span></p>
              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-white/5 py-3" data-testid="ideogram-month">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.ideogram?.month)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Images this month</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="ideogram-total">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.ideogram?.total)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">All time</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="ideogram-cost">
                  <p className="text-2xl font-head font-extrabold text-amber-300">{fmtMoney(data.ideogram?.est_cost_month)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Est. cost this month</p>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-3 flex items-center justify-between"><span>fal.ai motion clips — animated scenes (Hailuo 02)</span><span className="text-zinc-600">est. $0.27 / clip</span></p>
              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-white/5 py-3" data-testid="fal-motion-month">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.fal_motion?.month)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Clips this month</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="fal-motion-total">
                  <p className="text-2xl font-head font-extrabold">{fmtNum(data.fal_motion?.total)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">All time</p>
                </div>
                <div className="rounded-md bg-white/5 py-3" data-testid="fal-motion-cost">
                  <p className="text-2xl font-head font-extrabold text-amber-300">{fmtMoney(data.fal_motion?.est_cost_month)}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Est. cost this month</p>
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3">All-time est. spend: {fmtMoney((data.ideogram?.est_cost_total || 0) + (data.fal_motion?.est_cost_total || 0))}. Exact billing is on your Ideogram / fal.ai dashboards.</p>
            </div>
          </div>

          {/* Monthly special */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="monthly-special">
            <h2 className="font-head font-bold text-lg flex items-center gap-2">
              <Coins className="h-4 w-4 text-emerald-400" /> Monthly Special
              <span className="ml-auto text-xs font-normal text-zinc-500">shown on /pricing + in funnel offer emails</span>
            </h2>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                value={promoText}
                onChange={(e) => setPromoText(e.target.value)}
                placeholder='e.g. "Use code SUMMER20 for 20% off your first month"'
                data-testid="promo-text-input"
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => savePromo(true)}
                disabled={promoSaving || !promoText.trim()}
                data-testid="promo-activate-btn"
                className="bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {promoSaving ? "Saving…" : "Activate"}
              </button>
              <button
                onClick={() => savePromo(false)}
                disabled={promoSaving}
                data-testid="promo-deactivate-btn"
                className="bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
              >
                Turn off
              </button>
            </div>
            <p className="text-xs mt-2" data-testid="promo-status">
              {data.promo?.active ? (
                <span className="text-emerald-400">Live: “{data.promo.text}”</span>
              ) : (
                <span className="text-zinc-600">No special running — offer emails point to regular pricing.</span>
              )}
            </p>
          </div>

          {/* Newsletter growth + guide reads */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="subscriber-growth">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" /> Newsletter Growth
                <span className="ml-auto text-xs font-normal text-zinc-500">last 30 days</span>
              </h2>
              <div className="mt-4 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.newsletter_growth || []} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="subGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#27272a" }} interval={6} />
                    <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#a1a1aa" }}
                      formatter={(value, name) => [value, name === "total" ? "Total subscribers" : "Signups"]}
                    />
                    <Area type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} fill="url(#subGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="guide-reads">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-400" /> Top Guides by Reads
              </h2>
              {data.guide_views?.length > 0 ? (
                <div className="mt-4 space-y-2.5">
                  {data.guide_views.map((g, i) => (
                    <div key={g.slug} className="flex items-center gap-3" data-testid={`guide-read-row-${g.slug}`}>
                      <span className="text-xs text-zinc-600 w-4 text-right shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-zinc-300 truncate">{g.title}</p>
                        <div className="h-1.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.max(4, Math.round((g.views / (data.guide_views[0]?.views || 1)) * 100))}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-blue-300 shrink-0">{fmtNum(g.views)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500" data-testid="guide-reads-empty">
                  No reads counted yet — views appear as visitors open your guides.
                </p>
              )}
            </div>
          </div>

          {/* Blog: top articles + subscribers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="blog-reads">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-fuchsia-400" /> Top Blog Articles
                <span className="ml-auto text-xs font-normal text-zinc-500">
                  {fmtNum(data.blog?.total_posts || 0)} posts · {fmtNum(data.blog?.auto_published || 0)} auto-published
                </span>
              </h2>
              {data.blog?.top_posts?.length > 0 ? (
                <div className="mt-4 space-y-2.5">
                  {data.blog.top_posts.map((g, i) => (
                    <div key={g.slug} className="flex items-center gap-3" data-testid={`blog-read-row-${g.slug}`}>
                      <span className="text-xs text-zinc-600 w-4 text-right shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-zinc-300 truncate">{g.title}</p>
                        <div className="h-1.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                          <div
                            className="h-full bg-fuchsia-500 rounded-full"
                            style={{ width: `${Math.max(4, Math.round((g.views / (data.blog.top_posts[0]?.views || 1)) * 100))}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-fuchsia-300 shrink-0">{fmtNum(g.views)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500" data-testid="blog-reads-empty">
                  No reads counted yet — views appear as visitors open blog articles.
                </p>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="blog-subscribers">
              <h2 className="font-head font-bold text-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-400" /> Blog Signups
                <span className="ml-auto text-xs font-normal text-zinc-500">{fmtNum(data.blog?.subscribers || 0)} subscribers</span>
              </h2>
              {data.blog?.recent_subscribers?.length > 0 ? (
                <div className="mt-4 space-y-2.5">
                  {data.blog.recent_subscribers.map((s) => (
                    <div key={s.email} className="flex items-center gap-3" data-testid={`blog-sub-row-${s.email}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-zinc-300 truncate">{s.name ? `${s.name} · ` : ""}{s.email}</p>
                        <p className="text-xs text-zinc-600 truncate">
                          {(s.topics || []).filter((t) => t && t !== "general").join(", ") || "general"}
                        </p>
                      </div>
                      <span className="text-xs text-zinc-500 shrink-0">{fmtDate(s.created_at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500" data-testid="blog-subs-empty">
                  No blog signups yet — the email box inside each article collects them.
                </p>
              )}
            </div>
          </div>

          {/* Weekly digest stats */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="digest-stats">
            <h2 className="font-head font-bold text-lg flex items-center gap-2 flex-wrap">
              <Mail className="h-4 w-4 text-blue-400" /> Weekly Guide Digests
              <span className="ml-auto text-xs font-normal text-zinc-500">{fmtNum(data.newsletter_subscribers || 0)} newsletter subscribers</span>
              <button
                onClick={loadPreview}
                disabled={previewLoading}
                data-testid="digest-preview-btn"
                className="text-xs font-semibold bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
              >
                {previewLoading ? "Loading…" : "Preview email"}
              </button>
            </h2>
            {data.digests?.length > 0 ? (() => {
              const pct = (n, sent) => (sent > 0 ? Math.round((n / sent) * 100) : 0);
              const bestId = data.digests.reduce(
                (best, d) => ((d.sent || 0) >= 5 && pct(d.opens, d.sent) > pct(best?.opens || 0, best?.sent || 1) ? d : best),
                null
              )?.id;
              return (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="pb-2 font-medium">Sent</th>
                      <th className="pb-2 font-medium">Guide</th>
                      <th className="pb-2 font-medium text-right">Recipients</th>
                      <th className="pb-2 font-medium text-right">Opens</th>
                      <th className="pb-2 font-medium text-right">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.digests.map((d) => (
                      <tr key={d.id} className="border-t border-white/5" data-testid={`digest-row-${d.id}`}>
                        <td className="py-2.5 text-zinc-500">{(d.sent_at || "").slice(0, 10)}</td>
                        <td className="py-2.5 text-zinc-200 max-w-[280px] truncate">
                          {d.title}
                          {d.id === bestId && (
                            <span data-testid="digest-best-badge" className="ml-2 text-[9px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">
                              ★ Best open rate
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-right text-zinc-300">{fmtNum(d.sent)}</td>
                        <td className="py-2.5 text-right text-emerald-400" data-testid={`digest-opens-${d.id}`}>
                          {fmtNum(d.opens)} <span className="text-zinc-500 text-xs">({pct(d.opens, d.sent)}%)</span>
                        </td>
                        <td className="py-2.5 text-right text-blue-400" data-testid={`digest-clicks-${d.id}`}>
                          {fmtNum(d.clicks)} <span className="text-zinc-500 text-xs">({pct(d.clicks, d.sent)}%)</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              );
            })() : (
              <p className="mt-4 text-sm text-zinc-500" data-testid="digest-empty">
                No digests sent yet — the first weekly guide email will appear here with its open &amp; click stats.
              </p>
            )}
          </div>

          <SecurityCard pin={pin} onPinChanged={setPin} />

          <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="digest-preview-dialog">
              <DialogHeader>
                <DialogTitle className="font-head">Digest email preview</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Subject: <span className="text-zinc-200">{preview?.subject}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="bg-white text-zinc-900 rounded-md p-6 text-sm leading-relaxed max-h-[50vh] overflow-y-auto" data-testid="digest-preview-body">
                <p className="font-bold text-lg mb-3">{preview?.heading}</p>
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: preview?.html || "" }} />
              </div>
              <button
                onClick={sendTestDigest}
                disabled={testSending}
                data-testid="digest-test-send-btn"
                className="w-full text-sm bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60"
              >
                {testSending ? "Sending…" : "Send test to my inbox"}
              </button>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </div>
  );
}
